import React from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Button, Container, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import BookCallLink from '../components/BookCallLink';
import Eyebrow from '../components/Eyebrow';
import CallBand from '../components/stitch/CallBand';
import MonoChips from '../components/stitch/MonoChips';
import NumberedCards from '../components/stitch/NumberedCards';
import PageHero from '../components/stitch/PageHero';
import Section, { cardSx } from '../components/stitch/Section';
import { tokens } from '../theme';

// Built to the Stitch "Services" screen (project 1587770953174176333), with the site's own copy.

const linkSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  color: tokens.accentText,
  fontWeight: 600,
  fontSize: '0.875rem',
  textUnderlineOffset: '0.25em',
} as const;

function ArrowLink({ to, children, ...rest }: { to: string; children: React.ReactNode }) {
  return (
    <Box
      component={Link}
      to={to}
      sx={linkSx}
      {...rest}
    >
      {children}
      <ArrowForwardIcon
        aria-hidden='true'
        sx={{ fontSize: 16 }}
      />
    </Box>
  );
}

const services: {
  id: string;
  title: string;
  text: string;
  chips: string[];
  note?: string;
  links: { label: string; to: string }[];
}[] = [
  {
    id: 'web-applications',
    title: 'Custom web applications',
    text: 'Give people a clear way to complete complex tasks. We design customer inquiries, availability checks, administrative portals, and approval workflows around the information users need and the decisions they make.',
    chips: ['React', 'TypeScript', 'Next.js', '.NET', 'APIs'],
    links: [{ label: 'See application workflows', to: '/case-studies' }],
  },
  {
    id: 'content-platforms',
    title: 'Content platforms & CMS',
    text: 'Give editors a clear way to create, review, and publish content. We plan CMS tools around your sites, languages, roles, and approval process, with reusable content structures and connections to existing systems.',
    chips: ['Multi-site CMS', 'Editorial Roles', 'Multilingual Content', 'Media'],
    note: 'Our case studies distinguish predecessor publishing work from Scaled Tech delivery. InkFlow Studio is ongoing editorial-platform work; its production transition remains in progress.',
    links: [
      { label: 'Explore CMS case studies', to: '/case-studies' },
      { label: 'Explore CMS governance', to: '/cms-governance' },
    ],
  },
  {
    id: 'data-integrations',
    title: 'Data & integrations',
    text: 'Make existing information useful where people work. We build APIs and data models to connect applications with external feeds, imports, and the information teams need. Earlier projects include searchable travel feeds and menu-data imports for staff to edit.',
    chips: ['PostgreSQL', 'MySQL', 'SQL Server', 'XML', 'REST APIs'],
    links: [{ label: 'Explore earlier integration work', to: '/solutions' }],
  },
  {
    id: 'deployment-operations',
    title: 'Deployment & operations',
    text: 'Plan how an application will be built, released, and maintained. We help define repeatable deployments, hosting choices, and operational handoffs, with support responsibilities and terms agreed for the engagement.',
    chips: ['Docker', 'Cloudflare', 'Vercel', 'CI/CD'],
    links: [{ label: 'Read our website delivery practices', to: '/about-us' }],
  },
  {
    id: 'cross-platform-applications',
    title: 'Cross-platform applications',
    text: "Keep a workflow consistent across the devices people use. We plan interfaces, authentication, and data exchange around the needs of each platform. Our principal's earlier work includes mobile and web interfaces connected to enterprise services.",
    chips: ['Linux', 'Windows', 'macOS', 'Chrome', 'Firefox', 'Edge', 'Android', 'iOS'],
    links: [{ label: 'Explore earlier application work', to: '/solutions' }],
  },
];

const receive = [
  'Current-state workflow and editorial role map covering how content or requests move from preparation to publication.',
  'Architecture and data-flow diagram showing how your sites, applications, and connected systems fit together.',
  'Prioritized recommendations addressing governance, accessibility, or integration gaps.',
  'Written implementation roadmap and scoped proposal for next steps that your team can review before deciding on build work.',
];

function Services() {
  return (
    <Container>
      <PageHero
        eyebrow='Services'
        title='What we build'
        intro='Build the tools your team needs to manage content, connect information, and complete everyday work. We design web applications, CMS platforms, and integrations around the people using them and the systems they already rely on.'
      />

      <Box
        component='section'
        aria-labelledby='engagement-overview'
        sx={{
          bgcolor: tokens.surfaceLow,
          borderRadius: 3,
          p: { xs: 3, md: 5 },
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 5fr) minmax(0, 7fr)' },
          gap: { xs: 2, md: 5 },
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography
            variant='labelCaps'
            component='p'
            sx={{ color: tokens.accentText }}
          >
            Engagement principle
          </Typography>
          <Typography
            id='engagement-overview'
            component='h2'
            variant='h3'
            sx={{ mt: 1 }}
          >
            Start with your workflow
          </Typography>
        </Box>
        <Box>
          <Typography variant='body2'>
            We begin with your goals, users, and existing systems, then agree on a scope and
            proposal. Work progresses through reviewable build and test stages, with launch,
            handoff, and ongoing support planned for the engagement.
          </Typography>
          <Box
            component='a'
            href='#engagement-process'
            sx={{ ...linkSx, mt: 1.5 }}
          >
            See the delivery process
            <ArrowForwardIcon
              aria-hidden='true'
              sx={{ fontSize: 16 }}
            />
          </Box>
        </Box>
      </Box>

      <Box
        component='section'
        aria-labelledby='first-engagement'
        sx={{
          ...cardSx,
          mt: { xs: 5, md: 8 },
          p: { xs: 3, md: 5 },
          borderTop: `3px solid ${tokens.secondary}`,
          scrollMarginTop: '5.5rem',
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 7fr) minmax(0, 5fr)' },
          gap: { xs: 3, lg: 5 },
        }}
      >
        <Box>
          <Eyebrow>Defined first engagement</Eyebrow>
          <Typography
            id='first-engagement'
            component='h2'
            variant='h2'
            sx={{ mt: 2 }}
          >
            Start here: CMS &amp; Workflow Assessment
          </Typography>
          <Typography
            variant='body2'
            sx={{ mt: 2 }}
          >
            Whether you are improving an existing CMS or software platform, planning a new build, or
            connecting content with operational workflows, you can start with a bounded assessment
            before committing to a larger implementation.
          </Typography>
          <Typography
            variant='body2'
            sx={{ mt: 1.5 }}
          >
            The assessment is a fixed-scope engagement, typically 2 to 3 weeks, with a fixed fee
            quoted after a short call based on the systems and workflows in scope.
          </Typography>
          <Typography
            variant='bodySm'
            component='p'
            sx={{ mt: 1.5, color: tokens.onSurfaceVariant }}
          >
            After the assessment, your team keeps the written deliverables and decides whether to
            proceed with the proposed implementation scope.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 3 }}>
            <Button
              component={Link}
              to='/contact-us?subject=cms-workflow-assessment'
              data-track='assessment_request'
              variant='contained'
            >
              Request a CMS &amp; workflow assessment
            </Button>
            <BookCallLink />
          </Box>
        </Box>
        <Box sx={{ bgcolor: tokens.surfaceLow, borderRadius: 2, p: { xs: 2.5, md: 3 } }}>
          <Typography
            component='h3'
            variant='h5'
          >
            What you receive
          </Typography>
          <Box
            component='ul'
            sx={{ listStyle: 'none', m: 0, mt: 2, p: 0, display: 'grid', gap: 2 }}
          >
            {receive.map((item) => (
              <Box
                component='li'
                key={item}
                sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}
              >
                <CheckCircleOutlineIcon
                  aria-hidden='true'
                  sx={{ fontSize: 20, color: tokens.accentText, mt: '2px' }}
                />
                <Typography
                  variant='bodySm'
                  component='span'
                  sx={{ color: tokens.onSurface }}
                >
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          mt: { xs: 5, md: 8 },
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 3,
        }}
      >
        {services.map((service, index) => (
          <Box
            key={service.id}
            component='section'
            aria-labelledby={service.id}
            sx={{
              ...cardSx,
              p: { xs: 3, md: 4 },
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              // Five cards: the last one spans the row.
              gridColumn: { md: index === services.length - 1 ? '1 / -1' : 'auto' },
            }}
          >
            <Typography
              id={service.id}
              component='h2'
              variant='h4'
            >
              {service.title}
            </Typography>
            <Typography variant='body2'>{service.text}</Typography>
            <MonoChips items={service.chips} />
            {service.note && (
              <Typography
                variant='bodySm'
                component='p'
                sx={{ color: tokens.onSurfaceVariant }}
              >
                {service.note}
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mt: 'auto', pt: 1 }}>
              {service.links.map((link) => (
                <ArrowLink
                  key={link.label}
                  to={link.to}
                >
                  {link.label}
                </ArrowLink>
              ))}
            </Box>
          </Box>
        ))}
      </Box>

      <Section
        id='engagement-process'
        label='How engagements run'
        title='From inquiry to delivery'
        lead='Whether you are improving an existing CMS, planning a new platform, or looking for ongoing help, we shape the work around your goals and the people who use it.'
      >
        <NumberedCards
          items={[
            {
              title: 'Discovery and assessment',
              body: 'After your inquiry, we discuss your goals, users, existing systems, and constraints. Together we identify the problems to address and information needed for next steps.',
            },
            {
              title: 'Scope and proposal',
              body: 'If the work is a fit, we define a proposed scope, responsibilities, and approach for your review. Terms, timing, and costs are agreed for each engagement.',
            },
            {
              title: 'Build and test',
              body: 'We build in reviewable stages and agree on testing for the features, content, and accessibility requirements in scope. Feedback helps guide corrections before launch.',
            },
            {
              title: 'Launch and ongoing care',
              body: 'We plan release and handoff with your team, including ownership and operating notes. Maintenance and support are agreed according to the engagement.',
            },
          ]}
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 3 }}>
          <Button
            component={Link}
            to='/case-studies'
            variant='outlined'
          >
            See CMS case studies
          </Button>
          <Button
            component={Link}
            to='/contact-us'
            data-track='discuss_project'
            variant='contained'
          >
            Discuss your project
          </Button>
        </Box>
      </Section>

      <CallBand
        label='Next step'
        title='Talk through your project'
        text='Book a 20-minute introductory call with the person responsible for technical direction.'
        secondary={{ label: 'Send a message', to: '/contact-us#write-heading' }}
      />
    </Container>
  );
}

export default Services;
